import { useState } from "react";

import { Check, Download, Eye, FileText, Filter, Plus, X, MoreHorizontal, Paperclip, ToggleLeft, ToggleRight } from "lucide-react";
import moment from "moment";
import { toast } from "sonner";

import ActionMenu from "../../../components/shared/ActionMenu";
import CurrencyDisplay from "../../../components/shared/CurrencyDisplay";
import DataTable from "../../../components/shared/DataTable";
import LazySearchSelect from "../../../components/shared/LazySearchSelect";
import SecureImage from "../../../components/shared/SecureImage";
import DocumentViewer from "../../Photos/component/DocumentViewer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "../../../components/ui/alert-dialog";
import { Button } from "../../../components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { Switch } from "../../../components/ui/switch.jsx";
import { Label } from "../../../components/ui/label.jsx";
import { useApi } from "../../../context/ApiContext";
import { useAuth } from "../../../context/AuthContext";
import { getFileUrl } from "../../../lib/api";

const InvoicesTab = ({
  invoices,
  vendors,
  fetchInvoices,
  onEditInvoice,
  filters,
  onFilterChange,
  showDeleted,
  setShowDeleted,
  setInvoicePage
}) => {
  const { user } = useAuth();
  const api = useApi();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deletingInvoice, setDeletingInvoice] = useState(null);
  const [viewImage, setViewImage] = useState(null);

  const openDeleteDialog = (invoice) => {
    setDeletingInvoice(invoice);
    setIsDeleteOpen(true);
  };

  const deleteInvoice = async () => {
    try {
      const response = await api.delete(`/invoices/${deletingInvoice.id}`);
      if (response.status === 200 || response.status === 204) {
        toast.success("Invoice deleted successfully!");
        setIsDeleteOpen(false);
        setDeletingInvoice(null);
        fetchInvoices();
      } else {
        toast.error("Failed to delete invoice");
      }
    } catch (error) {
      toast.error(error.message || "Error deleting invoice");
    }
  };

  const handleApproveInvoice = async (invoice) => {
    try {
      const response = await api.post(`/invoices/${invoice.id}/approve`);
      if (response.status === 200) {
        toast.success("Invoice approved successfully! Cash payment created.");
        fetchInvoices();
      } else {
        toast.error("Failed to approve invoice");
      }
    } catch (error) {
      toast.error(error.message || "Error approving invoice");
    }
  };

  const handleDenyInvoice = async (invoice) => {
    try {
      const response = await api.post(`/invoices/${invoice.id}/deny`);
      if (response.status === 200) {
        toast.success("Invoice denied successfully!");
        fetchInvoices();
      } else {
        toast.error("Failed to deny invoice");
      }
    } catch (error) {
      toast.error(error.message || "Error denying invoice");
    }
  };

  const handleDownloadPdf = async (invoice) => {
    try {
      toast.loading("Generating PDF...", { id: "pdf-toast" });
      const response = await api.get(`/invoices/${invoice.id}/pdf`, {
        responseType: "blob"
      });

      // Create blob URL and download
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invoice-${invoice.invoiceNumber || invoice.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded successfully!", { id: "pdf-toast" });
    } catch (error) {
      toast.error(error.message || "Failed to download PDF", {
        id: "pdf-toast"
      });
    }
  };

  const invoiceColumns = [
    {
      header: "Invoice #",
      accessor: "invoiceNumber",
      isPrimary: true,
      render: (val, invoice) => (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-yale-blue-900 dark:text-slate-100">{val || invoice.id}</span>
          {invoice.deleted && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] font-extrabold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 uppercase tracking-wider shrink-0">
              Deleted
            </span>
          )}
        </div>
      )
    },
    {
      header: "Vendor",
      filter: (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`p-1 rounded-md transition-colors ${filters.vendorId !== "all" ? "bg-primary/20 text-primary" : "hover:bg-slate-200 dark:hover:bg-yale-blue-800 text-slate-400"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Filter className="w-3 h-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-4" align="start">
            <div className="space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Filter by Vendor</p>
              <LazySearchSelect
                value={filters.vendorId}
                onChange={(val) => onFilterChange("vendor", val)}
                apiUrl="/vendors"
                labelKey="vendorName"
                placeholder="Select vendor"
                searchPlaceholder="Search vendors..."
                showAllOption={true}
                allOptionLabel="All Vendors"
                allOptionValue="all"
              />
            </div>
          </PopoverContent>
        </Popover>
      ),
      accessor: "vendor",
      render: (val) => (
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{val?.vendorName || "Unknown"}</span>
      )
    },
    {
      header: "Project",
      accessor: "project",
      render: (val) => <span className="text-xs font-medium text-slate-600 dark:text-slate-400">{val?.name || "N/A"}</span>
    },
    {
      header: "Date",
      accessor: "invoiceDate",
      sortable: true,
      sortKey: "invoiceDate",
      render: (val) => moment(val).format("DD/MM/YYYY"),
      cellClassName: "text-sm text-slate-600 dark:text-slate-400"
    },
    {
      header: "Amount",
      accessor: "amount",
      render: (val) => (
        <span className="font-mono font-bold text-yale-blue-600 dark:text-yale-blue-400">
          <CurrencyDisplay amount={val} />
        </span>
      )
    },
    {
      header: "Created By",
      accessor: "creator",
      render: (val) => <span className="text-xs font-medium text-slate-600 dark:text-slate-400">{val?.name || "System"}</span>
    },
    {
      header: "Status",
      filter: (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`p-1 rounded-md transition-colors ${filters.status !== "all" ? "bg-primary/20 text-primary" : "hover:bg-slate-200 dark:hover:bg-yale-blue-800 text-slate-400"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Filter className="w-3 h-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-1" align="end">
            {[
              { label: "All Status", value: "all" },
              { label: "Pending", value: "pending" },
              { label: "Approved", value: "approved" },
              { label: "Denied", value: "denied" }
            ].map((s) => (
              <Button
                key={s.value}
                variant={filters.status === s.value ? "secondary" : "ghost"}
                className="w-full justify-start text-sm font-normal h-9 px-3"
                onClick={() => onFilterChange("status", s.value)}
              >
                {s.label}
              </Button>
            ))}
          </PopoverContent>
        </Popover>
      ),
      accessor: "status",
      render: (val) => (
        <span
          className={`inline-flex px-2.5 py-0.5 text-[10px] font-bold rounded-full uppercase tracking-wider ${
            val === "paid"
              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
              : val === "partially_paid"
                ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"
                : val === "approved"
                  ? "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400"
                  : val === "denied"
                    ? "bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-400"
                    : "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400"
          }`}
        >
          {val?.replace("_", " ")}
        </span>
      )
    },
    {
      header: "Actions",
      filter: user?.permissions?.invoice?.delete ? (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`p-1 rounded-md transition-colors ${showDeleted ? "bg-primary/20 text-primary" : "hover:bg-slate-200 dark:hover:bg-yale-blue-800 text-slate-400"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="w-4 h-4 hidden md:block" />
              <span className="md:hidden">
                {showDeleted ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-3" align="end">
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <Switch
                checked={showDeleted}
                onCheckedChange={(val) => {
                  setShowDeleted(val);
                  setInvoicePage(0);
                }}
                id="show-deleted-invoices-table"
              />
              <Label htmlFor="show-deleted-invoices-table" className="text-xs font-semibold cursor-pointer whitespace-nowrap">
                Show Deleted
              </Label>
            </div>
          </PopoverContent>
        </Popover>
      ) : null,
      accessor: "id",
      render: (_, invoice) => {
        const isPending = !invoice.status || invoice.status === "pending";
        const canApprove = user?.permissions?.invoice?.approve || user?.role === "super_admin";
        const attachmentUrls = invoice.attachmentUrls || (invoice.attachment ? [invoice.attachment] : []);
        const hasAttachments = attachmentUrls.length > 0;

        return (
          <div className="flex items-center gap-2">
            {hasAttachments && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2 gap-1 text-yale-blue-600 dark:text-yale-blue-400 hover:text-yale-blue-700 dark:hover:text-yale-blue-300 rounded-full hover:bg-yale-blue-50 dark:hover:bg-yale-blue-900/20 flex items-center"
                onClick={() => {
                  const urls = attachmentUrls.map((path) => getFileUrl(path));
                  setViewImage(urls);
                }}
                title={`View ${attachmentUrls.length} attachment(s)`}
              >
                <Paperclip className="h-4 w-4" />
                <span className="text-xs font-semibold">{attachmentUrls.length}</span>
              </Button>
            )}

            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-slate-500 hover:text-slate-700"
              onClick={() => handleDownloadPdf(invoice)}
              title="Download PDF"
            >
              <Download className="h-4 w-4" />
            </Button>

            {isPending && canApprove && !invoice.deleted && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-950/20"
                  onClick={() => handleApproveInvoice(invoice)}
                  title="Approve Invoice"
                >
                  <Check className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20"
                  onClick={() => handleDenyInvoice(invoice)}
                  title="Reject/Deny Invoice"
                >
                  <X className="h-4 w-4" />
                </Button>
              </>
            )}

            <ActionMenu
              canEdit={user?.permissions?.invoice?.update && !invoice.deleted}
              canDelete={user?.permissions?.invoice?.delete && !invoice.deleted}
              onEdit={() => onEditInvoice(invoice)}
              onDelete={() => openDeleteDialog(invoice)}
            />
          </div>
        );
      }
    }
  ];

  return (
    <div className="flex-1 flex flex-col space-y-4">
      <DataTable
        columns={invoiceColumns}
        data={invoices}
        emptyStateTitle="No invoices found"
        emptyStateDescription="Upload and track vendor invoices here."
        emptyStateIcon={FileText}
        className="flex-1"
        sortBy={filters.sortBy}
        direction={filters.direction}
        onSort={(key, dir) => {
          onFilterChange("sortBy", key);
          onFilterChange("direction", dir);
        }}
      />

      {/* Delete Invoice Confirmation Dialog */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the invoice "{deletingInvoice?.invoiceNumber}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteInvoice} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* View Attachment Modal */}
      <DocumentViewer
        isOpen={!!viewImage}
        onClose={() => setViewImage(null)}
        data={viewImage}
      />
    </div>
  );
};

export default InvoicesTab;
